import Checkbox from '@wui/input/checkbox';
import Icon from '@material-ui/core/Icon';
import React from 'react';
import WizardPage from 'ui/components/wizard/WizardPage';
import { NotarizationStatus } from 'utils/enums';
import { NotarizationWizardPageMixin } from 'ui/components/wizard/NotarizationWizardPageMixin';
import { API } from 'API';
import Box from '@material-ui/core/Box';
import Typography from '@wui/basics/typography';
import WuiApp from 'material-ui/WuiApp';
import Layout from '@c/layout/Layout';
import Grid from '@wui/layout/grid';
import Button from '@wui/input/button';
import TextField from '@material-ui/core/TextField';
import { WFullName } from 'models/hoc-types';
import { validatePersonNameUniqueness, runTypeValidation } from '!m/utils';
import './IDNameValidation.scss';

export default class IDNameValidation extends NotarizationWizardPageMixin(WizardPage) {
  static baseUrl = 'id-name-validation/';
  static showPrevNextButtons = false;
  static notarizationSection = NotarizationStatus.ID_NAME_VALIDATION;
  static enterNavigation = false;
  static forSpouse = false;

  constructor(props, context) {
    super(props, context);
    this.state = {
      name: !this.constructor.forSpouse
        ? this.context.clientDataHolder.clientData.name
        : this.context.clientDataHolder.clientData.spouse.name,
      errors: {},
      loading: false,
      success: false,
      idUrls: null,
      testatorString: !this.constructor.forSpouse ? 'client' : 'spouse',
      expandedId: 'front',
      doesNameMatch: false,
    };
  }

  componentDidMount() {
    this.fetchImageUrls();
  }

  fetchImageUrls = () => {
    API.getIdUrls().then(result => {
      this.setState({ idUrls: result.data });
    });
  };

  validateName = nameValue => {
    const errors = {};
    const fullNameErrors = runTypeValidation(WFullName, nameValue);

    if (fullNameErrors.length > 0) {
      errors.format = fullNameErrors[0];
    }

    const testator = this.constructor.forSpouse
      ? this.context.clientDataHolder.clientData.spouse
      : this.context.clientDataHolder.clientData;

    const uniquenessErrors = validatePersonNameUniqueness(
      this.context.clientDataHolder.clientData,
      { ...testator, name: nameValue },
      false,
    )();

    if (uniquenessErrors.length > 0) {
      errors.uniqueness = uniquenessErrors[0];
    }

    return errors;
  };

  onChange = e => {
    const value = e.target.value;
    this.setState({
      name: value,
      errors: this.validateName(value),
    });
  };

  onClick = () => {
    const { name } = this.state;
    this.setState({ loading: true });
    if (this.constructor.forSpouse) {
      this.context.clientDataHolder.clientData.spouse.name = name;
    } else {
      this.context.clientDataHolder.clientData.name = name;
    }

    this.context.clientDataHolder.persistClientData().then(response => {
      if (!response.success && response.error) {
        if (response.error === 'excess_name_changes') {
          this.setState({
            errors: {
              message:
                'Looks like your name has been changed too many times. Please contact us so we can help you change it.',
            },
          });
        } else {
          this.setState({ success: false, errors: { message: response.error } });
        }
      }
      this.setState({ success: true, loading: false });
    });
  };

  renderActionButtons = () => {
    if (this.state) {
      return (
        <React.Fragment>
          <Button
            color="primary"
            variant="contained"
            style={{ marginTop: '16px', width: '100%' }}
            disabled={!this.state.doesNameMatch}
            onClick={() => this.goToNext()}
          >
            Continue
          </Button>
        </React.Fragment>
      );
    }

    return null;
  };

  renderErrors() {
    const { errors } = this.state;

    return Object.values(errors).map(message => (
      <Typography key={message} component="span" variant="caption">
        {message}
      </Typography>
    ));
  }

  renderIconAdornmant() {
    const { errors, loading, success } = this.state;
    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      return <Icon style={{ color: 'red' }}>error</Icon>;
    }

    if (loading) {
      return <Icon>data_usage</Icon>;
    }

    if (success) {
      return <Icon style={{ color: 'green' }}>check</Icon>;
    }

    return null;
  }

  renderPage() {
    const { name, errors, idUrls, testatorString, expandedId, doesNameMatch } = this.state;

    return (
      <>
        {idUrls && (
          <WuiApp wrapContent={false}>
            <Layout
              wide
              hideRight
              showBackButton
              useDigexProgress
              buttonRenderer={this.renderActionButtons}
            >
              <Grid container direction="row" spacing={2}>
                <Grid item>
                  <Typography variant="h2">Let's start by verifying your name</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2">
                    Your name on the estate planning documents must exactly match your ID. Check
                    below and update either if they differ.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box className="mui-warning-box">
                    <Icon style={{ color: '#EF6C00', marginRight: '16px' }}>warning</Icon>
                    <Typography style={{ color: '#7B3306' }}>
                      <strong>Warning:</strong> The notary will not be able to verify your identity
                      and execute your documents if these names do not match exactly.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="mui-white-box">
                    <Grid container spacing={2} direction="row" alignItems="center">
                      <Grid item>
                        <Typography
                          style={{ fontWeight: 600, fontSize: '18px', marginBottom: '16px' }}
                        >
                          Name in Estate Planning Documents
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container direction="row" alignItems="top" spacing={2}>
                      <Grid item xs={8} style={{ marginTop: '76px' }}>
                        <TextField
                          id="full-name"
                          defaultValue={name}
                          error={Object.keys(errors).length > 0}
                          variant="outlined"
                          label="Full Name"
                          style={{ backgroundColor: 'white', width: '100%' }}
                          onChange={e => {
                            const value = e.target.value;
                            this.setState({
                              name: value,
                              errors: this.validateName(value),
                              success: false,
                            });
                          }}
                          InputProps={{
                            endAdornment: this.renderIconAdornmant(),
                          }}
                        />
                        {Object.keys(errors).length > 0 && this.renderErrors()}
                      </Grid>
                      <Grid item>
                        <Button
                          variant="outlined"
                          className="mui-name-button"
                          disabled={Object.keys(errors).length > 0}
                          onClick={() => this.onClick()}
                        >
                          Update
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="mui-white-box">
                    <Grid container spacing={2} direction="row">
                      <Grid item>
                        <Typography
                          style={{ fontWeight: 600, fontSize: '18px', marginBottom: '16px' }}
                        >
                          Name on ID
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container direction="row" spacing={2}>
                      <Grid item xs={8} alignContent="flex-end">
                        <img
                          alt="Customer ID"
                          src={idUrls[testatorString][expandedId]}
                          style={{ width: '100%' }}
                        />
                      </Grid>
                      <Grid item xs alignContent="flex-end">
                        <Button
                          variant="outlined"
                          className="mui-name-button"
                          onClick={() => this.props.goToPrev()}
                        >
                          Re-capture ID
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box className="mui-check-name-box">
                    <Grid container spacing={1}>
                      <Grid item>
                        <Typography style={{ fontWeight: 600 }}>
                          Do these names match exactly?
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Checkbox
                          label="Yes, the names match exactly"
                          checked={doesNameMatch}
                          onChange={e => this.setState({ doesNameMatch: e.target.checked })}
                        />
                      </Grid>
                      <Grid item>
                        <Typography variant="subtitle2">
                          If the names do not match, please update either the name in your documents
                          or ID above.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Layout>
          </WuiApp>
        )}
      </>
    );
  }
}
